import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Radio, Plus, Play, Square, Trash2, Copy, RefreshCw, Eye, Users, Clock, BarChart3, Loader2,
  Download, UserPlus, Search, ShieldCheck, ShieldX, LogOut, KeyRound, Activity, ExternalLink,
  Settings as SettingsIcon, Check, FileDown, Link2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const useApi = () => {
  const token = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
  return axios.create({ baseURL: API, headers: { Authorization: `Bearer ${token}` } });
};

// Robust copy — handles browsers without clipboard API or denied permission.
const copyToClipboard = async (text) => {
  const value = String(text || '');
  if (!value) { toast.error('Nothing to copy'); return false; }
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (_e) { /* fall through to legacy path */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    ta.style.pointerEvents = 'none';
    document.body.appendChild(ta);
    ta.focus(); ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch (_e) {
    return false;
  }
};

const fmt = (s) => {
  const n = Number(s || 0);
  if (n < 60) return `${n}s`;
  if (n < 3600) return `${Math.floor(n / 60)}m ${n % 60}s`;
  return `${Math.floor(n / 3600)}h ${Math.floor((n % 3600) / 60)}m`;
};

const statusColor = (s) => ({
  draft: 'bg-slate-200 text-slate-800',
  scheduled: 'bg-blue-100 text-blue-800 border-blue-300',
  live: 'bg-red-100 text-red-700 border-red-400 animate-pulse',
  ended: 'bg-slate-100 text-slate-600',
}[s] || 'bg-slate-100');

// ============== MAIN PAGE ==============
export default function LiveStreamingPage() {
  const api = useApi();
  const [stats, setStats] = useState(null);
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [accessOpen, setAccessOpen] = useState(null);   // stream object
  const [activeTab, setActiveTab] = useState('dashboard');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState({ rtmp_base_url: '', hls_url_template: '' });

  const loadSettings = useCallback(async () => {
    try { const r = await api.get('/livestream/settings'); setSettings(r.data || {}); }
    catch { /* ignore */ }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([api.get('/livestream/dashboard'), api.get('/livestream/streams')]);
      setStats(s.data);
      setStreams(l.data?.streams || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);
  // Refresh every 30s for live-counter sense
  useEffect(() => { const t = setInterval(loadAll, 30000); return () => clearInterval(t); }, [loadAll]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2 text-slate-800">
            <Radio className="w-6 h-6 text-rose-600" /> Live Streaming
          </h1>
          <p className="text-sm text-slate-500">OBS broadcast with ITS-based access · single-session enforced · HLS playback</p>
        </div>
        <Button onClick={() => { setEditing(null); setCreateOpen(true); }} className="bg-rose-600 hover:bg-rose-700" data-testid="new-stream-btn">
          <Plus className="w-4 h-4 mr-1" /> New Stream
        </Button>
        <Button onClick={() => setSettingsOpen(true)} variant="outline" data-testid="settings-btn">
          <SettingsIcon className="w-4 h-4 mr-1" /> Settings
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="streams" data-testid="tab-streams">Streams</TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">Viewer Logs</TabsTrigger>
          <TabsTrigger value="sessions" data-testid="tab-sessions">Active Sessions</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="space-y-3">
          <DashboardCards stats={stats} loading={loading} />
        </TabsContent>

        <TabsContent value="streams">
          <StreamsTable streams={streams} loading={loading}
            onRefresh={loadAll}
            onEdit={(s) => { setEditing(s); setCreateOpen(true); }}
            onAccess={(s) => setAccessOpen(s)}
          />
        </TabsContent>

        <TabsContent value="logs">
          <ViewerLogsTable streams={streams} />
        </TabsContent>

        <TabsContent value="sessions">
          <ActiveSessionsTable />
        </TabsContent>
      </Tabs>

      <StreamFormDialog
        open={createOpen}
        editing={editing}
        settings={settings}
        onClose={() => { setCreateOpen(false); setEditing(null); }}
        onSaved={() => { setCreateOpen(false); setEditing(null); loadAll(); }}
      />
      {accessOpen && <AccessControlDialog stream={accessOpen} onClose={() => setAccessOpen(null)} />}
      <SettingsDialog open={settingsOpen} settings={settings} onClose={() => setSettingsOpen(false)} onSaved={(s) => { setSettings(s); setSettingsOpen(false); }} />
    </div>
  );
}

// ============== SETTINGS DIALOG ==============
const SettingsDialog = ({ open, settings, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({
    rtmp_base_url: '', hls_url_template: '',
    cloudflare_account_id: '', cloudflare_api_token: '',
    use_cloudflare: false,
  });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  useEffect(() => {
    if (open) setF({
      rtmp_base_url: settings.rtmp_base_url || '',
      hls_url_template: settings.hls_url_template || '',
      cloudflare_account_id: settings.cloudflare_account_id || '',
      cloudflare_api_token: '',  // never prefill — masked on backend
      use_cloudflare: !!settings.use_cloudflare,
    });
  }, [open, settings]);

  const submit = async () => {
    setBusy(true);
    try {
      // Only send token if user typed one; otherwise omit so backend keeps the existing.
      const body = { ...f };
      if (!body.cloudflare_api_token) delete body.cloudflare_api_token;
      const r = await api.patch('/livestream/settings', body);
      toast.success('Settings saved');
      onSaved(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const testCf = async () => {
    setTesting(true);
    try {
      const r = await api.post('/livestream/cloudflare/test');
      toast.success(`Cloudflare OK — ${r.data.live_inputs_count} existing live input(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Cloudflare test failed'); }
    finally { setTesting(false); }
  };

  const [migrating, setMigrating] = useState(false);
  const migrateAll = async () => {
    if (!window.confirm("Create Cloudflare live inputs for every stream that doesn't have one yet? Existing RTMP URL + Stream Key + HLS URL will be replaced with Cloudflare values.")) return;
    setMigrating(true);
    try {
      const r = await api.post('/livestream/streams/migrate-to-cloudflare');
      const m = r.data?.migrated_count || 0;
      const f = r.data?.failed_count || 0;
      const skip = r.data?.already_on_cloudflare || 0;
      if (m > 0) toast.success(`Migrated ${m} stream(s) to Cloudflare`);
      if (skip > 0) toast.info(`${skip} stream(s) already on Cloudflare`);
      if (f > 0) toast.error(`${f} stream(s) failed — check console`);
      if (f > 0) console.log('Failed migrations:', r.data.failed);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Migration failed'); }
    finally { setMigrating(false); }
  };

  const [repairing, setRepairing] = useState(false);
  const repairPlayback = async () => {
    setRepairing(true);
    try {
      const r = await api.post('/livestream/streams/repair-playback-urls');
      const n = r.data?.repaired_count || 0;
      const ok = r.data?.already_correct_count || 0;
      const sub = r.data?.customer_subdomain;
      if (sub) toast.success(`Subdomain: ${sub} — fixed ${n}, already-correct ${ok}`);
      else toast.success(`Fixed ${n} stream(s), ${ok} already correct`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Repair failed'); }
    finally { setRepairing(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SettingsIcon className="w-5 h-5 text-rose-600" /> Live Streaming Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* CLOUDFLARE STREAM */}
          <div className="bg-amber-50 border border-amber-200 rounded-md p-3 space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={!!f.use_cloudflare} onChange={(e) => setF({ ...f, use_cloudflare: e.target.checked })} data-testid="settings-use-cf" />
              <span className="font-semibold text-amber-900">Use Cloudflare Stream Live (recommended)</span>
            </label>
            <p className="text-xs text-amber-800">When enabled, every new stream auto-creates a Cloudflare live input. You don't need your own RTMP server. OBS pushes directly to Cloudflare (rtmps://live.cloudflare.com:443/live/).</p>
            <div>
              <Label className="text-xs">Cloudflare Account ID</Label>
              <Input value={f.cloudflare_account_id} onChange={(e) => setF({ ...f, cloudflare_account_id: e.target.value.trim() })}
                placeholder="32-char hex from dash.cloudflare.com sidebar" data-testid="settings-cf-account" />
            </div>
            <div>
              <Label className="text-xs flex items-center justify-between">
                <span>Cloudflare API Token (Account → Stream → Edit)</span>
                {settings.cloudflare_api_token_set && (
                  <span className="text-[11px] text-emerald-700 font-normal">Configured ({settings.cloudflare_api_token_masked || '••••'})</span>
                )}
              </Label>
              <Input type="password" value={f.cloudflare_api_token} onChange={(e) => setF({ ...f, cloudflare_api_token: e.target.value.trim() })}
                placeholder={settings.cloudflare_api_token_set ? 'Leave blank to keep existing token' : 'Paste your API token'}
                data-testid="settings-cf-token" />
              <div className="text-[11px] text-amber-700 mt-1">Get one at <a href="https://dash.cloudflare.com/profile/api-tokens" target="_blank" rel="noopener noreferrer" className="underline">dash.cloudflare.com/profile/api-tokens</a> — Custom Token → Stream:Edit.</div>
            </div>
            <Button size="sm" variant="outline" onClick={testCf} disabled={testing || !settings.cloudflare_api_token_set} className="w-full" data-testid="settings-cf-test">
              {testing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Activity className="w-3 h-3 mr-1" />} Test Cloudflare Connection
            </Button>
            <Button size="sm" variant="outline" onClick={migrateAll} disabled={migrating || !settings.cloudflare_api_token_set || !settings.use_cloudflare} className="w-full text-amber-800 border-amber-400" data-testid="settings-cf-migrate">
              {migrating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Migrate Existing Streams to Cloudflare
            </Button>
            <Button size="sm" variant="outline" onClick={repairPlayback} disabled={repairing || !settings.cloudflare_api_token_set || !settings.use_cloudflare} className="w-full text-emerald-800 border-emerald-400" data-testid="settings-cf-repair">
              {repairing ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />} Repair Playback URLs (fixes "manifestLoadError")
            </Button>
          </div>

          {/* SELF-HOSTED */}
          <div className="border border-slate-200 rounded-md p-3 space-y-3">
            <div className="text-xs uppercase tracking-wide font-semibold text-slate-600">Self-Hosted nginx-rtmp (fallback)</div>
            <p className="text-xs text-slate-600">Used only when Cloudflare is OFF. Placeholder <code className="bg-slate-100 px-1 rounded">{'{key}'}</code> is replaced with each stream's generated key.</p>
            <div>
              <Label className="text-xs">RTMP Server URL (base)</Label>
              <Input value={f.rtmp_base_url} onChange={(e) => setF({ ...f, rtmp_base_url: e.target.value })}
                placeholder="rtmp://stream.smngp.com/live" data-testid="settings-rtmp" />
            </div>
            <div>
              <Label className="text-xs">HLS Playback URL (template)</Label>
              <Input value={f.hls_url_template} onChange={(e) => setF({ ...f, hls_url_template: e.target.value })}
                placeholder="https://stream.smngp.com/hls/{key}/index.m3u8" data-testid="settings-hls" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="settings-save">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Check className="w-4 h-4 mr-1" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== DASHBOARD CARDS ==============
const DashboardCards = ({ stats, loading }) => {
  const items = [
    { label: 'Total Streams', value: stats?.total_streams ?? '—', icon: Radio, color: 'text-rose-700 bg-rose-50' },
    { label: 'Live Now', value: stats?.live_streams ?? '—', icon: Activity, color: 'text-red-700 bg-red-50' },
    { label: 'Concurrent Viewers', value: stats?.concurrent_viewers ?? '—', icon: Eye, color: 'text-emerald-700 bg-emerald-50' },
    { label: 'Unique Viewers', value: stats?.unique_viewers ?? '—', icon: Users, color: 'text-blue-700 bg-blue-50' },
    { label: 'Total Sessions', value: stats?.total_sessions ?? '—', icon: BarChart3, color: 'text-violet-700 bg-violet-50' },
    { label: 'Total Watch Hours', value: stats?.total_watch_hours ?? '—', icon: Clock, color: 'text-amber-700 bg-amber-50' },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {items.map((it, i) => (
        <Card key={i} className="border-slate-200">
          <CardContent className="p-3 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-md flex items-center justify-center ${it.color}`}>
              <it.icon className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs text-slate-500">{it.label}</div>
              <div className="text-xl font-bold text-slate-800">{loading ? '…' : it.value}</div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== STREAMS TABLE ==============
const StreamsTable = ({ streams, loading, onRefresh, onEdit, onAccess }) => {
  const api = useApi();
  const setStatus = async (s, status) => {
    try { await api.patch(`/livestream/streams/${s.id}`, { status }); toast.success(`Status → ${status}`); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const regenKey = async (s) => {
    if (!window.confirm('Regenerate stream key? OBS will need to be reconfigured.')) return;
    try { const r = await api.post(`/livestream/streams/${s.id}/regenerate-key`); toast.success(`New key: ${r.data.stream_key}`); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (s) => {
    if (!window.confirm(`Delete "${s.title}"? This removes access lists and viewer logs too.`)) return;
    try { await api.delete(`/livestream/streams/${s.id}`); toast.success('Deleted'); onRefresh(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const copy = async (txt, label) => {
    const ok = await copyToClipboard(txt);
    if (ok) toast.success(`${label} copied`);
    else toast.error(`Copy failed — long-press the text to copy manually`);
  };
  const downloadObsProfile = async (s) => {
    try {
      const t = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
      const r = await fetch(`${API}/livestream/streams/${s.id}/obs-profile`, { headers: { Authorization: `Bearer ${t}` } });
      if (!r.ok) { toast.error('Failed to generate OBS profile'); return; }
      const blob = await r.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = u; a.download = `SMNGP_${(s.title || 'Stream').replace(/[^A-Za-z0-9_-]+/g, '_')}.zip`;
      a.click(); URL.revokeObjectURL(u);
      toast.success('OBS profile downloaded — extract into your OBS profiles folder');
    } catch { toast.error('Download failed'); }
  };
  const copyViewerLink = async (s) => {
    // Universal viewer entry — same link for all streams. After login the user lands
    // on their dashboard which lists every stream they're authorised to watch.
    const link = `${window.location.origin}/live`;
    const ok = await copyToClipboard(link);
    if (ok) toast.success('Viewer link copied — share with authorised members');
    else toast.error('Copy failed');
  };

  if (loading) return <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-rose-600" /></div>;
  if (streams.length === 0) return <Card><CardContent className="p-10 text-center text-slate-500">No streams yet. Click <strong>New Stream</strong> to create one.</CardContent></Card>;

  return (
    <div className="space-y-3">
      {streams.map((s) => (
        <Card key={s.id} className="border-slate-200" data-testid={`stream-card-${s.id}`}>
          <CardContent className="p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-800 truncate">{s.title}</h3>
                  <Badge className={`text-xs uppercase ${statusColor(s.status)}`}>{s.status}</Badge>
                  {s.category && <Badge variant="outline" className="text-xs">{s.category}</Badge>}
                </div>
                {s.description && <p className="text-sm text-slate-600 mt-0.5">{s.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 flex-wrap">
                  {s.stream_date && <span>📅 {s.stream_date} {s.start_time || ''}{s.end_time ? ` – ${s.end_time}` : ''}</span>}
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{s.access_count} granted</span>
                  <span className="inline-flex items-center gap-1 text-emerald-700"><Eye className="w-3 h-3" />{s.live_viewer_count} live now</span>
                </div>
              </div>
              <div className="flex gap-1 flex-wrap">
                {s.status !== 'live' && <Button size="sm" variant="outline" onClick={() => setStatus(s, 'live')} className="text-red-600 border-red-300" data-testid={`go-live-${s.id}`}><Play className="w-3 h-3 mr-1" />Go Live</Button>}
                {s.status === 'live' && <Button size="sm" variant="outline" onClick={() => setStatus(s, 'ended')} className="text-slate-700"><Square className="w-3 h-3 mr-1" />End</Button>}
                <Button size="sm" variant="outline" onClick={() => onAccess(s)} data-testid={`access-${s.id}`}><ShieldCheck className="w-3 h-3 mr-1" />Access</Button>
                <Button size="sm" variant="outline" onClick={() => copyViewerLink(s)} data-testid={`viewer-link-${s.id}`}><Link2 className="w-3 h-3 mr-1" />Viewer Link</Button>
                <Button size="sm" variant="outline" onClick={() => downloadObsProfile(s)} data-testid={`obs-profile-${s.id}`}><FileDown className="w-3 h-3 mr-1" />OBS Profile</Button>
                <Button size="sm" variant="outline" onClick={() => onEdit(s)} data-testid={`edit-${s.id}`}>Edit</Button>
                <Button size="sm" variant="outline" onClick={() => del(s)} className="text-red-600"><Trash2 className="w-3 h-3" /></Button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-3 bg-slate-50 border rounded-md p-3 text-sm">
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">OBS — RTMP Server URL</div>
                <div className="flex items-center gap-1">
                  <code className="flex-1 text-xs bg-white border rounded px-2 py-1 font-mono truncate">{s.rtmp_url || '(not set)'}</code>
                  {s.rtmp_url && <Button size="sm" variant="ghost" onClick={() => copy(s.rtmp_url, 'RTMP URL')}><Copy className="w-3 h-3" /></Button>}
                </div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">OBS — Stream Key</div>
                <div className="flex items-center gap-1">
                  <code className="flex-1 text-xs bg-white border rounded px-2 py-1 font-mono truncate">{s.stream_key}</code>
                  <Button size="sm" variant="ghost" onClick={() => copy(s.stream_key, 'Stream key')}><Copy className="w-3 h-3" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => regenKey(s)} title="Regenerate"><RefreshCw className="w-3 h-3" /></Button>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold mb-1">HLS Playback URL</div>
                <div className="flex items-center gap-1">
                  <code className="flex-1 text-xs bg-white border rounded px-2 py-1 font-mono truncate">{s.playback_url || '(not set)'}</code>
                  {s.playback_url && (<>
                    <Button size="sm" variant="ghost" onClick={() => copy(s.playback_url, 'Playback URL')}><Copy className="w-3 h-3" /></Button>
                    <a href={s.playback_url} target="_blank" rel="noopener noreferrer"><Button size="sm" variant="ghost"><ExternalLink className="w-3 h-3" /></Button></a>
                  </>)}
                </div>
              </div>
              <div className="md:col-span-2 bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-900 leading-relaxed">
                <div className="font-semibold mb-1">OBS Settings to avoid viewer buffering (Cloudflare HLS):</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  <li><strong>Keyframe Interval = 2 seconds</strong> (Output → Streaming) — most important.</li>
                  <li><strong>B-frames = 0</strong> · <strong>Rate Control = CBR</strong> · <strong>Preset = veryfast</strong></li>
                  <li><strong>Bitrate</strong>: 2500 kbps for 720p · 4000 kbps for 1080p</li>
                  <li><strong>FPS = 30</strong> (Settings → Video — not 60)</li>
                  <li>Enable <strong>Network Optimisations</strong> under Advanced → Network</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// ============== CREATE/EDIT STREAM DIALOG ==============
const StreamFormDialog = ({ open, editing, settings, onClose, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({
    title: '', description: '', category: '', stream_date: '', start_time: '', end_time: '',
    rtmp_url: '', playback_url: '', thumbnail_url: '', banner_url: '', status: 'draft',
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) setF({ ...editing });
    else if (settings?.use_cloudflare) {
      // Cloudflare path — RTMP server is the public Cloudflare endpoint; key + HLS
      // come back from the API the moment you click Create.
      setF({
        title: '', description: '', category: '', stream_date: '', start_time: '', end_time: '',
        rtmp_url: 'rtmps://live.cloudflare.com:443/live/',
        playback_url: '',
        thumbnail_url: '', banner_url: '', status: 'draft',
      });
    } else {
      setF({
        title: '', description: '', category: '', stream_date: '', start_time: '', end_time: '',
        rtmp_url: settings?.rtmp_base_url || '',
        playback_url: settings?.hls_url_template || '',
        thumbnail_url: '', banner_url: '', status: 'draft',
      });
    }
  }, [open, editing, settings]);

  const submit = async () => {
    if (!f.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (editing) await api.patch(`/livestream/streams/${editing.id}`, f);
      else await api.post('/livestream/streams', f);
      toast.success(editing ? 'Updated' : 'Stream created');
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="stream-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Radio className="w-5 h-5 text-rose-600" /> {editing ? 'Edit Stream' : 'New Stream'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Title *</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="form-title" /></div>
          <div><Label>Description</Label><Textarea rows={2} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Category</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} placeholder="Waaz / Ashara / Ramzan" /></div>
            <div><Label>Status</Label>
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="ended">Ended</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><Label>Date</Label><Input type="date" value={f.stream_date} onChange={(e) => setF({ ...f, stream_date: e.target.value })} /></div>
            <div><Label>Start</Label><Input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} /></div>
            <div><Label>End</Label><Input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} /></div>
          </div>
          <div className="bg-slate-50 border rounded-md p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs uppercase font-semibold text-slate-500">
                {settings?.use_cloudflare ? 'Cloudflare Stream — OBS Configuration' : 'OBS / nginx-rtmp Configuration'}
              </div>
              {settings?.use_cloudflare && (
                <span className="text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-300 rounded px-1.5 py-0.5">Cloudflare Stream ON</span>
              )}
              {!settings?.use_cloudflare && !settings?.rtmp_base_url && !settings?.hls_url_template && (
                <span className="text-[11px] text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">Tip: set defaults in Settings to auto-fill these</span>
              )}
            </div>

            {settings?.use_cloudflare ? (
              <>
                <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900 leading-relaxed">
                  ✓ A unique Cloudflare live input will be created the moment you click <strong>Create stream</strong>. The actual <strong>RTMP URL</strong>, <strong>Stream Key</strong> and <strong>HLS Playback URL</strong> appear on the stream card right after — copy them into OBS, or click <strong>OBS Profile</strong> for a one-click import.
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div>
                    <Label className="text-xs">OBS → Server (read-only)</Label>
                    <Input value="rtmps://live.cloudflare.com:443/live/" readOnly className="bg-white font-mono text-[12px]" />
                  </div>
                  <div>
                    <Label className="text-xs">OBS → Stream Key</Label>
                    <Input value="Auto-generated on Create" readOnly className="bg-white text-slate-500 text-[12px] italic" />
                  </div>
                </div>
                <div className="text-[11px] text-slate-500">In OBS → Settings → Stream: select <strong>Custom</strong>, paste the Server URL above, and the per-stream Stream Key from the card after save.</div>
              </>
            ) : (
              <>
                <div><Label>RTMP Server URL</Label><Input placeholder="rtmp://stream.smngp.com/live" value={f.rtmp_url} onChange={(e) => setF({ ...f, rtmp_url: e.target.value })} data-testid="form-rtmp" /></div>
                <div><Label>HLS Playback URL</Label><Input placeholder="https://stream.smngp.com/hls/{key}/index.m3u8" value={f.playback_url} onChange={(e) => setF({ ...f, playback_url: e.target.value })} data-testid="form-hls" /></div>
                <div className="text-[11px] text-slate-500">Stream key is auto-generated and shown after save. In OBS → Settings → Stream: select <strong>Custom</strong>, paste the RTMP URL above as <strong>Server</strong>, and the generated <strong>Stream Key</strong>.</div>
              </>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="submit-stream">
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
            {editing ? 'Save changes' : 'Create stream'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============== ACCESS CONTROL DIALOG ==============
const AccessControlDialog = ({ stream, onClose }) => {
  const api = useApi();
  const [tab, setTab] = useState('list');
  const [list, setList] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Grant forms
  const [grantIts, setGrantIts] = useState('');
  const [grantMohalla, setGrantMohalla] = useState('');
  const [grantSector, setGrantSector] = useState('');
  const [grantWhole, setGrantWhole] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/livestream/access/${stream.id}${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ''}`);
      setList(r.data?.access || []);
    } catch { /* ignore */ } finally { setLoading(false); }
    // eslint-disable-next-line
  }, [stream.id, q]);
  useEffect(() => { load(); }, [load]);

  const grant = async () => {
    const its = grantIts.split(/[,\s\n]+/).map(s => s.trim()).filter((s) => /^\d+$/.test(s));
    if (its.length === 0 && !grantMohalla && !grantSector && !grantWhole) {
      toast.error('Provide ITS IDs, or pick a mohalla/sector/whole jamaat');
      return;
    }
    setBusy(true);
    try {
      const r = await api.post('/livestream/access/grant', {
        stream_id: stream.id, its_ids: its,
        mohalla: grantMohalla || null, sector: grantSector || null, whole_jamaat: grantWhole,
      });
      toast.success(`Granted ${r.data.granted} ITS · ${r.data.newly_added} new`);
      setGrantIts(''); setGrantMohalla(''); setGrantSector(''); setGrantWhole(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const revoke = async (its_id) => {
    if (!window.confirm(`Revoke ${its_id}'s access?`)) return;
    try {
      await api.post('/livestream/access/revoke', { stream_id: stream.id, its_ids: [its_id] });
      toast.success('Revoked'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const resetPwd = async (its_id) => {
    if (!window.confirm(`Reset ${its_id}'s password to their ITS ID?`)) return;
    try { await api.post(`/livestream/admin/reset-password/${its_id}`); toast.success('Password reset to ITS'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="access-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-rose-600" /> Access · {stream.title}</DialogTitle>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="list" data-testid="access-tab-list">List ({list.length})</TabsTrigger>
            <TabsTrigger value="grant" data-testid="access-tab-grant"><UserPlus className="w-3 h-3 mr-1" />Grant</TabsTrigger>
          </TabsList>

          <TabsContent value="list" className="space-y-2">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 w-4 h-4 text-slate-400" />
              <Input className="pl-8" placeholder="Search ITS / Name / Mohalla" value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            <div className="border rounded-md overflow-hidden max-h-96 overflow-y-auto">
              {loading ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin" /></div> :
                list.length === 0 ? <div className="p-6 text-center text-slate-500 text-sm">No access granted yet.</div> :
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 text-xs text-slate-600 sticky top-0">
                      <tr>
                        <th className="px-2 py-1.5 text-left">ITS</th>
                        <th className="px-2 py-1.5 text-left">Name</th>
                        <th className="px-2 py-1.5 text-left">Mohalla</th>
                        <th className="px-2 py-1.5 text-left">Sector</th>
                        <th className="px-2 py-1.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {list.map((a) => (
                        <tr key={a.its_id}>
                          <td className="px-2 py-1.5 font-mono text-xs">{a.its_id}</td>
                          <td className="px-2 py-1.5">{a.name || '—'}</td>
                          <td className="px-2 py-1.5">{a.mohalla || '—'}</td>
                          <td className="px-2 py-1.5">{a.sector || '—'}</td>
                          <td className="px-2 py-1.5 text-right">
                            <Button size="sm" variant="ghost" onClick={() => resetPwd(a.its_id)} title="Reset password"><KeyRound className="w-3 h-3" /></Button>
                            <Button size="sm" variant="ghost" onClick={() => revoke(a.its_id)} title="Revoke" className="text-red-600"><ShieldX className="w-3 h-3" /></Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </div>
          </TabsContent>

          <TabsContent value="grant" className="space-y-3">
            <div>
              <Label>ITS IDs (one per line or comma-separated)</Label>
              <Textarea rows={4} placeholder="50462579&#10;40400291&#10;..." value={grantIts} onChange={(e) => setGrantIts(e.target.value)} data-testid="grant-its-textarea" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label>Mohalla (entire)</Label><Input placeholder="NAJMI MOHALLA" value={grantMohalla} onChange={(e) => setGrantMohalla(e.target.value)} /></div>
              <div><Label>Sector (entire)</Label><Input placeholder="SECTOR 8" value={grantSector} onChange={(e) => setGrantSector(e.target.value)} /></div>
            </div>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={grantWhole} onChange={(e) => setGrantWhole(e.target.checked)} data-testid="grant-whole-jamaat" />
              Grant to <strong>entire jamaat</strong> (all members)
            </label>
            <Button onClick={grant} disabled={busy} className="bg-rose-600 hover:bg-rose-700 w-full" data-testid="grant-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <UserPlus className="w-4 h-4 mr-1" />} Grant Access
            </Button>
            <div className="text-[11px] text-slate-500">First-time login password defaults to the viewer's ITS ID. They will be required to change it.</div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

// ============== VIEWER LOGS TAB ==============
const ViewerLogsTable = ({ streams }) => {
  const api = useApi();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filt, setFilt] = useState({ stream_id: '', its_id: '', mohalla: '', sector: '' });

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filt).forEach(([k, v]) => { if (v) params.append(k, v); });
      const r = await api.get(`/livestream/logs?${params.toString()}&limit=2000`);
      setLogs(r.data?.logs || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const exportXlsx = async () => {
    const t = localStorage.getItem('access_token') || localStorage.getItem('token') || '';
    const params = filt.stream_id ? `?stream_id=${filt.stream_id}` : '';
    const r = await fetch(`${API}/livestream/export/logs${params}`, { headers: { Authorization: `Bearer ${t}` } });
    const blob = await r.blob();
    const u = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = u; a.download = 'LiveStream_Logs.xlsx'; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base flex items-center justify-between">
        <span>Viewer Logs ({logs.length})</span>
        <Button size="sm" variant="outline" onClick={exportXlsx} data-testid="export-logs-btn"><Download className="w-3 h-3 mr-1" />Export Excel</Button>
      </CardTitle></CardHeader>
      <CardContent className="space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
          <Select value={filt.stream_id || 'all'} onValueChange={(v) => setFilt({ ...filt, stream_id: v === 'all' ? '' : v })}>
            <SelectTrigger><SelectValue placeholder="All streams" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All streams</SelectItem>
              {streams.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="ITS ID" value={filt.its_id} onChange={(e) => setFilt({ ...filt, its_id: e.target.value })} />
          <Input placeholder="Mohalla" value={filt.mohalla} onChange={(e) => setFilt({ ...filt, mohalla: e.target.value })} />
          <Input placeholder="Sector" value={filt.sector} onChange={(e) => setFilt({ ...filt, sector: e.target.value })} />
          <Button onClick={load} disabled={loading} className="bg-rose-600 hover:bg-rose-700">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}</Button>
        </div>
        <div className="border rounded-md overflow-hidden max-h-[60vh] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs sticky top-0">
              <tr>
                <th className="px-2 py-1.5 text-left">Stream</th>
                <th className="px-2 py-1.5 text-left">ITS</th>
                <th className="px-2 py-1.5 text-left">Name</th>
                <th className="px-2 py-1.5 text-left">Mohalla</th>
                <th className="px-2 py-1.5 text-left">Login</th>
                <th className="px-2 py-1.5 text-left">Logout</th>
                <th className="px-2 py-1.5 text-right">Duration</th>
                <th className="px-2 py-1.5 text-left">Device</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="px-2 py-1.5 truncate max-w-[120px]">{l.stream_title}</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{l.its_id}</td>
                  <td className="px-2 py-1.5">{l.name || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{l.mohalla || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{l.login_at?.slice(0, 19).replace('T', ' ')}</td>
                  <td className="px-2 py-1.5 text-xs">{l.logout_at?.slice(0, 19).replace('T', ' ') || '—'}</td>
                  <td className="px-2 py-1.5 text-right text-xs">{fmt(l.duration_seconds)}</td>
                  <td className="px-2 py-1.5 text-xs">{l.device} / {l.browser}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No logs.</td></tr>}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

// ============== ACTIVE SESSIONS TAB ==============
const ActiveSessionsTable = () => {
  const api = useApi();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/livestream/admin/active-sessions'); setRows(r.data?.sessions || []); }
    catch { /* ignore */ }
    finally { setLoading(false); }
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 10000); return () => clearInterval(t); }, [load]);

  const forceLogout = async (its) => {
    if (!window.confirm(`Force logout ITS ${its}?`)) return;
    try { await api.post(`/livestream/admin/force-logout/${its}`); toast.success('Logged out'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Active Viewer Sessions ({rows.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs">
              <tr>
                <th className="px-2 py-1.5 text-left">ITS</th>
                <th className="px-2 py-1.5 text-left">Name</th>
                <th className="px-2 py-1.5 text-left">Mohalla</th>
                <th className="px-2 py-1.5 text-left">Watching</th>
                <th className="px-2 py-1.5 text-left">Device</th>
                <th className="px-2 py-1.5 text-left">IP</th>
                <th className="px-2 py-1.5 text-left">Since</th>
                <th className="px-2 py-1.5 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading && <tr><td colSpan={8} className="p-4 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-slate-500">No active sessions.</td></tr>}
              {rows.map((r) => (
                <tr key={r.its_id}>
                  <td className="px-2 py-1.5 font-mono text-xs">{r.its_id}</td>
                  <td className="px-2 py-1.5">{r.name || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{r.mohalla || '—'}</td>
                  <td className="px-2 py-1.5 text-xs">{r.current_stream_id || <span className="text-slate-400">—</span>}</td>
                  <td className="px-2 py-1.5 text-xs">{r.device} / {r.browser}</td>
                  <td className="px-2 py-1.5 text-xs font-mono">{r.ip}</td>
                  <td className="px-2 py-1.5 text-xs">{r.issued_at?.slice(11, 19)}</td>
                  <td className="px-2 py-1.5 text-right">
                    <Button size="sm" variant="ghost" onClick={() => forceLogout(r.its_id)} className="text-red-600" data-testid={`force-logout-${r.its_id}`}><LogOut className="w-3 h-3" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
